import { CellClassRules, ColDef, ColGroupDef } from 'ag-grid-enterprise'

import { DataGridColumn, DataGridRow } from '@fortem-monorepo/web-sdk'

import { DataGridColumnDef, DataGridContentBlock } from '@/components/content-blocks/blocks/data-grid/types'
import { formatNumber } from '@/utilities/format'

type DataGridValueFormatter<TRow extends DataGridRow> = ColDef<TRow>['valueFormatter']

export const resolveDataGridColumnDefs = <TRow extends DataGridRow>(
  block: DataGridContentBlock<TRow>,
  overrideColumnDefs?: DataGridColumnDef<TRow>[],
): DataGridColumnDef<TRow>[] => {
  const defaultColumnDefs = createDefaultDataGridColumnDefs(block)

  if (!overrideColumnDefs?.length) {
    return defaultColumnDefs
  }

  return applyBackendColumnMetadata(overrideColumnDefs, block)
}

export const createDefaultDataGridColumnDefs = <TRow extends DataGridRow>(
  block: DataGridContentBlock<TRow>,
): DataGridColumnDef<TRow>[] => {
  return block.columns.map((column) => ({
    field: column.name as ColDef<TRow>['field'],
    headerName: column.label,
    valueFormatter: createDataGridValueFormatter(column),
    cellClassRules: createDataGridHighlightRules(column),
    ...(column.roles?.includes('hidden') && { hide: true }),
  }))
}

export const applyBackendColumnMetadata = <TRow extends DataGridRow>(
  columnDefs: DataGridColumnDef<TRow>[],
  block: DataGridContentBlock<TRow>,
): DataGridColumnDef<TRow>[] => {
  const columnsByName = new Map(block.columns.map((column) => [column.name, column]))

  return columnDefs.map((columnDef) => applyColumnMetadata(columnDef, columnsByName))
}

const applyColumnMetadata = <TRow extends DataGridRow>(
  columnDef: DataGridColumnDef<TRow>,
  columnsByName: Map<string, DataGridColumn>,
): DataGridColumnDef<TRow> => {
  if (isColumnGroupDef(columnDef)) {
    return {
      ...columnDef,
      children: columnDef.children.map((child) => applyColumnMetadata(child, columnsByName)),
    }
  }

  const column = columnDef.field ? columnsByName.get(columnDef.field) : undefined

  if (!column) {
    return columnDef
  }

  return {
    headerName: column.label,
    valueFormatter: createDataGridValueFormatter(column),
    cellClassRules: createDataGridHighlightRules(column),
    ...(column.roles?.includes('hidden') && { hide: true }),
    ...columnDef,
  }
}

const isColumnGroupDef = <TRow extends DataGridRow>(
  columnDef: DataGridColumnDef<TRow>,
): columnDef is ColGroupDef<TRow> => {
  return 'children' in columnDef && Array.isArray(columnDef.children)
}

const createDataGridHighlightRules = <TRow extends DataGridRow>(
  column: DataGridColumn,
): CellClassRules<TRow> | undefined => {
  if (column.type !== 'number' || !column.highlight_rules?.length) return undefined

  const classRules: CellClassRules<TRow> = {}
  const tones = { negative: 'text-red-700', positive: 'text-emerald-700' } as const

  for (const tone of ['negative', 'positive'] as const) {
    const rules = column.highlight_rules.filter((rule) => rule.tone === tone)
    if (!rules.length) continue

    classRules[tones[tone]] = ({ value }) =>
      typeof value === 'number' &&
      Number.isFinite(value) &&
      rules.some((rule) => {
        if (!Number.isFinite(rule.value)) return false
        if (rule.operator === 'lt') return value < rule.value
        if (rule.operator === 'gt') return value > rule.value
        return false
      })
  }

  return classRules
}

const createDataGridValueFormatter = <TRow extends DataGridRow>(
  column: DataGridColumn,
): DataGridValueFormatter<TRow> => {
  switch (column.format) {
    case 'percentage':
      return ({ value }) =>
        typeof value === 'number' ? formatNumber(value, { suffix: '%', decimalPlaces: 2, type: 'percentage' }) : value
    case 'basis_points':
      return ({ value }) =>
        typeof value === 'number'
          ? formatNumber(value, { suffix: ' bps', decimalPlaces: 2, type: 'basis_points' })
          : value
    case 'currency':
      return ({ value }) =>
        typeof value === 'number' ? formatNumber(value, { decimalPlaces: 2, type: 'unit' }) : value
    default:
      if (column.type === 'number') {
        return ({ value }) =>
          typeof value === 'number' ? formatNumber(value, { decimalPlaces: 2, type: 'unit' }) : value
      }

      return undefined
  }
}
